import React from "react";
import { Droppable, DroppableProvided } from "react-beautiful-dnd";
import styled from "styled-components";
import { Divider, IconButton, Typography } from "@mui/material";
import { IConfigStorage } from "../rule-engine-core/Common/IConfigStorage";
import { DataUtilities } from "../rule-engine-core/Common/DataUtilities";
import { FormControl } from "./Controls/FormControl";
import { XBaseContainer } from "./Containers/XBaseContainer";
import {
    BorderStyle,
    ButtonControlSize,
    FormDesignConstants,
    FormMode,
    LinkControlType,
    LinkControlUnderline,
    TextControlSize,
    TextControlStyle,
} from "./Utilities/FormDesignConstants";
import { UIControlRegistration } from "./Utilities/RegisterUIControl";
import { IEditable } from "../CommonUI/IEditable";
import { PropertyEditorInteger } from "../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorBoolean } from "../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorSelect } from "../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorColor } from "../CommonUI/PropertyEditing/PropertyEditorColor";
import { XStackContainer } from "./Containers/XStackContainer";

import {
    PropertyEditorTextSizeSelection,
    PropertyEditorTextStyleSelection,
} from "../CommonUI/PropertyEditing/PropertyEditorTextStyleSelection";
import { PinIcon } from "../CommonUI/Icon/PinIcon";
import { ScrollIcon } from "../CommonUI/Icon/ScrollIcon";
import { XContainerDesignWrapper } from "./Utilities/XContainerDesignWrapper";
import { FormContext } from "./Utilities/FormContext";
import { MultiExecutable, MultiExecutableOutputDefinition } from "../rule-engine-core/Common/Execution/MultiExecutable";
import { ExecutionData } from "../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ButtonFormControl } from "./Controls/Web/ButtonFormControl";
import { LinkFormControl } from "./Controls/Web/LinkFormControl";
import { FormRule, FormRuleType } from "./Structure/FormRule";
import { BooleanDataDefinition } from "../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { IDataDefinition } from "../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataDefinition } from "../rule-engine-core/Common/Data/DataDefinition";
import { ValidationIssue } from "../rule-engine-core/Common/IDesignValidationProvider";
import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { ExpressionConfiguration } from "../rule-engine-core/Expressions/ExpressionConfiguration";
import { UIStyleConstants } from "../rule-engine-core/Common/UIStyleConstants";
import { ITestingContext } from "../rule-engine-core/Common/Testing/ITestingContext";
import { ExecutableReturnPromise } from "../rule-engine-core/Common/Execution/ExecutableReturnPromise";
import { AppGlueCoreInit } from "../AppGlueCoreInit";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { PropertyEditorCollapse } from "../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { ContainerDiv } from "../CommonUI/CommonStyles/ContainerDiv";
import { RowDiv } from "../CommonUI/CommonStyles/RowDiv";
import { ContainerDivider } from "../CommonUI/CommonStyles/ContainerDivider";
import { UISpacedBoxContent } from "../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import type { AppGlueAny } from "../rule-engine-core/Common/AppGlueAny";

export const PinnedNotifyDiv = styled("div")<{
    color?: string;
    moveRightPX?: number;
    borderWidth?: number;
}>`
    position: absolute;
    right: calc(100% + ${(props) => (props.moveRightPX ? props.moveRightPX + 34 : 34)}px);
    border-right: solid ${(props) => props.borderWidth || 2}px
        ${(props) => props.color || UIStyleConstants.BLUE_GRAY_COLOR};
    display: flex;
    align-items: center;
    height: 100%;
`;

export const ContainerSectionDiv = styled("div")<{
    pinned?: boolean;
}>`
    position: ${(props) => (props.pinned ? "relative" : "static")};
`;

export class FormConfiguration extends MultiExecutable implements IConfigStorage, IEditable {
    _id: string = DataUtilities.generateUniqueId();
    title?: string;
    designFormWidth: number = FormDesignConstants.DESIGN_WIDTH;
    // setting this to -1 means not set
    runtimeWidth?: number | null = FormDesignConstants.RUNTIME_WIDTH;
    gapBetweenContainers: number = FormDesignConstants.GAP_BETWEEN_CONTAINERS;
    formMargin: number = FormDesignConstants.FORM_MARGIN;
    defaultInnerContainerMargin: number = FormDesignConstants.INNER_CONTAINER_MARGIN;
    defaultInnerColumnMargin?: number = FormDesignConstants.INNER_COLUMN_MARGIN;
    defaultInterControlSpacing: number = FormDesignConstants.INTER_CONTROL_SPACING;
    // first container
    doNotScrollLastContainerOnForm: boolean = false;
    doNotScrollFirstContainerOnForm: boolean = false;
    defaultShowContainerBorder?: boolean = FormDesignConstants.SHOW_CONTAINER_BORDER;
    defaultContainerBorderRadius: number = FormDesignConstants.CONTAINER_BORDER_RADIOUS;
    defaultContainerBorderWidth: number = FormDesignConstants.CONTAINER_BORDER_WIDTH;
    defaultContainerBorderColor: string = FormDesignConstants.CONTAINER_BORDER_COLOR;
    defaultContainerBorderStyle: BorderStyle = FormDesignConstants.CONTAINER_BORDER_STYLE;
    showLinesBetweenContainers?: boolean = false;
    defaultLineBetweenContainerWidth: number = FormDesignConstants.CONTAINER_BORDER_WIDTH;
    defaultLineBetweenContainerColor: string = FormDesignConstants.CONTAINER_BORDER_COLOR;
    defaultLineBetweenContainerStyle: BorderStyle = FormDesignConstants.CONTAINER_BORDER_STYLE;
    formBackgroundColor: string | null = FormDesignConstants.FORM_BACKGROUND_COLOR;
    defaultContainerBackgroundColor: string | null = FormDesignConstants.CONTAINER_BACKGROUND_COLOR;
    defaultTextStyle: TextControlStyle = FormDesignConstants.DEFAULT_TEXT_STYLE;
    defaultTextSize: TextControlSize = FormDesignConstants.DEFAULT_TEXT_SIZE;
    defaultButtonSize: ButtonControlSize = FormDesignConstants.DEFAULT_BUTTON_SIZE;
    defaultLinkUnderline: LinkControlUnderline = FormDesignConstants.DEFAULT_LINK_UNDERLINE;
    defaultLinkType: LinkControlType = FormDesignConstants.DEFAULT_LINK_TYPE;
    private containers: XBaseContainer[] = [];
    // this is either designer or runtime form
    private _formContext: FormContext | null = null;

    constructor(container?: XBaseContainer[]) {
        super();

        // ensure controls are registered.
        AppGlueCoreInit.init();

        if (!this._formContext) {
            this._formContext = new FormContext(this);
            this._formContext.mode = FormMode.Runtime;
        }

        this.containers = container ?? [];

        if (this.containers) {
            for (let bc of this.containers) {
                if (this._formContext) bc.setFormContext(this._formContext);
            }
        }

        StateManager.addObserver(this, () => this.refreshDesigner());
    }

    private _rules: FormRule[] = [];

    get rules(): FormRule[] {
        return this._rules;
    }

    set rules(rules: FormRule[]) {
        this._rules = rules;

        for (let rule of rules) {
            rule.expression.inputData = () => {
                return this.getFormDatDefinition();
            };
            rule.expression.executionResult = new BooleanDataDefinition();
        }
    }

    getFormContext(): FormContext | null {
        return this._formContext;
    }

    setFormContext(value: FormContext | null) {
        if (!value) throw new Error("cannot set form context to null (Form Config)");

        this._formContext = value;

        if (value) {
            for (let cont of this.containers) {
                cont.setFormContext(value);
            }
        }
    }

    add(container: XBaseContainer, index?: number) {
        // set control order
        if (index === undefined || index === null) {
            this.containers.push(container);
        } else {
            this.containers.splice(index, 0, container);
        }

        if (this.getFormContext()) {
            container.setFormContext(this.getFormContext()!);
        }
    }

    remove(control: FormControl): void {
        const index = (this.containers as FormControl[]).indexOf(control, 0);
        if (index > -1) {
            this.containers.splice(index, 1);
        } else {
            for (let cont of this.containers) {
                cont.remove(control);
            }
        }
    }

    setOrder(id: string, order: number) {
        let controlToMove: XBaseContainer | undefined;
        for (let c of this.containers) {
            if (c.id === id) {
                controlToMove = c;
            }
        }

        if (controlToMove) {
            this.remove(controlToMove);
            this.add(controlToMove, order);
        }
    }

    getContainers(): XBaseContainer[] {
        return this.containers;
    }

    getMode(): FormMode | string {
        return this.getFormContext()?.mode ?? FormMode.Runtime;
    }

    getWidth(): number {
        return this.getMode() === FormMode.Runtime && !!this.runtimeWidth ? this.runtimeWidth : this.designFormWidth;
    }

    getContentWidth(): number {
        const formWidth = this.getWidth();
        return formWidth - 2 * this.formMargin;
    }

    isDevice(): boolean {
        return (
            [
                FormDesignConstants.FORM_MODE_TABLET_VERTICAL,
                FormDesignConstants.FORM_MODE_TABLET_HORIZONTAL,
                FormDesignConstants.FORM_MODE_PHONE_VERTICAL,
                FormDesignConstants.FORM_MODE_PHONE_HORIZONTAL,
            ].indexOf(this.getFormContext()?.designConfig?.mode || "") >= 0
        );
    }

    getFormDatDefinition(): DataDefinition {
        const ruleDataDef = new DataDefinition();
        const dataDef = this.getInputDefinition();
        const formDataDefFeilds = this.getAllFormData();
        ruleDataDef.fields = [...dataDef.fields, ...formDataDefFeilds];

        const formValue = this.getFormContext()?.getFormData().values;
        if (formValue) {
            ruleDataDef.value = { ...dataDef.value, ...formValue };
        } else {
            ruleDataDef.value = dataDef.value;
        }
        return ruleDataDef;
    }

    @AutoBind
    render() {
        // if there are no containers, ensure we have at least one (stack)
        if (this.containers.length === 0) {
            this.add(new XStackContainer());
        }
        let mode = this.getMode();

        const formStyles: { [key: string]: AppGlueAny } = {
            padding: this.formMargin,
            backgroundColor: this.formBackgroundColor ? this.formBackgroundColor : UIStyleConstants.WHITE_COLOR,
            position: "relative",
            minHeight: 2 * this.gapBetweenContainers + 75,
        };

        let firstContainer: FormControl | null = null;
        let lastContainer: FormControl | null = null;

        let scrollableContrainers: XBaseContainer[] = this.containers.map((c) => c);

        if (this.doNotScrollFirstContainerOnForm) {
            firstContainer = this.containers[0];
            scrollableContrainers.splice(0, 1);
        }

        if (this.doNotScrollLastContainerOnForm && this.containers.length > 1) {
            lastContainer = this.containers[this.containers.length - 1];
            scrollableContrainers.splice(-1);
        }

        const pinned =
            (this.doNotScrollFirstContainerOnForm || this.doNotScrollLastContainerOnForm) &&
            scrollableContrainers.length > 0;

        const gap = this.gapBetweenContainers / 2;

        if (mode === FormMode.LayoutDesign) {
            return (
                <div
                    key="baseformcontainer"
                    className="App"
                    style={formStyles}
                >
                    <Droppable
                        droppableId={FormDesignConstants.LAYOUT_FORM_KEY_NAME}
                        key={"baselayoutform"}
                    >
                        {(provided: DroppableProvided) => {
                            return (
                                <div
                                    ref={provided.innerRef}
                                    style={{ position: "relative", minHeight: 75 }}
                                    {...provided.droppableProps}
                                >
                                    <RowDiv
                                        colGap={gap}
                                        hasOverflow={
                                            !(
                                                this.doNotScrollLastContainerOnForm ||
                                                this.doNotScrollFirstContainerOnForm
                                            )
                                        }
                                    >
                                        {firstContainer && (
                                            <ContainerSectionDiv pinned={true}>
                                                <PinnedNotifyDiv moveRightPX={this.formMargin}>
                                                    <IconButton
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                        size="large"
                                                    >
                                                        <PinIcon />
                                                    </IconButton>
                                                </PinnedNotifyDiv>
                                                <ContainerDiv colGap={gap}>
                                                    <XContainerDesignWrapper
                                                        id={firstContainer.id}
                                                        index={0}
                                                        innerComponent={firstContainer}
                                                        editContext={this.getFormContext()!}
                                                    />
                                                </ContainerDiv>
                                            </ContainerSectionDiv>
                                        )}
                                        <ContainerSectionDiv pinned={pinned}>
                                            {pinned && (
                                                <PinnedNotifyDiv
                                                    color={UIStyleConstants.RED_DARK_COLOR}
                                                    moveRightPX={this.formMargin}
                                                >
                                                    <IconButton
                                                        color="secondary"
                                                        size="large"
                                                    >
                                                        <ScrollIcon />
                                                    </IconButton>
                                                </PinnedNotifyDiv>
                                            )}
                                            {scrollableContrainers.map((c: XBaseContainer, i: number) => {
                                                return (
                                                    <React.Fragment key={c.id}>
                                                        {this.showLinesBetweenContainers &&
                                                            (firstContainer || i !== 0) && (
                                                                <ContainerDivider
                                                                    data-testid="line-between-containers"
                                                                    lineColorBetweenContainers={
                                                                        this.defaultLineBetweenContainerColor
                                                                    }
                                                                    lineStyleBetweenContrainers={
                                                                        this.defaultLineBetweenContainerStyle
                                                                    }
                                                                    lineWidthBetweenContainers={
                                                                        this.defaultLineBetweenContainerWidth
                                                                    }
                                                                    colGap={gap}
                                                                />
                                                            )}
                                                        <ContainerDiv colGap={gap}>
                                                            <XContainerDesignWrapper
                                                                id={c.id}
                                                                index={i}
                                                                innerComponent={c}
                                                                editContext={this.getFormContext()!}
                                                            />
                                                        </ContainerDiv>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </ContainerSectionDiv>
                                        {lastContainer && (
                                            <>
                                                {this.showLinesBetweenContainers && (
                                                    <ContainerDivider
                                                        data-testid="line-between-containers"
                                                        lineColorBetweenContainers={
                                                            this.defaultLineBetweenContainerColor
                                                        }
                                                        lineStyleBetweenContrainers={
                                                            this.defaultLineBetweenContainerStyle
                                                        }
                                                        lineWidthBetweenContainers={
                                                            this.defaultLineBetweenContainerWidth
                                                        }
                                                        colGap={gap}
                                                    />
                                                )}
                                                <ContainerSectionDiv pinned={true}>
                                                    <PinnedNotifyDiv moveRightPX={this.formMargin}>
                                                        <IconButton
                                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                            size="large"
                                                        >
                                                            <PinIcon />
                                                        </IconButton>
                                                    </PinnedNotifyDiv>
                                                    <ContainerDiv colGap={gap}>
                                                        <XContainerDesignWrapper
                                                            id={lastContainer.id}
                                                            index={this.containers.length - 1}
                                                            innerComponent={lastContainer}
                                                            editContext={this.getFormContext()!}
                                                        />
                                                    </ContainerDiv>
                                                </ContainerSectionDiv>
                                            </>
                                        )}
                                        {provided.placeholder}
                                    </RowDiv>
                                </div>
                            );
                        }}
                    </Droppable>
                </div>
            );
        } else if (
            (this.isDevice() || mode === FormMode.Runtime) &&
            (this.doNotScrollLastContainerOnForm || this.doNotScrollFirstContainerOnForm) &&
            this.containers.length > 0
        ) {
            formStyles.height = "100%";

            return (
                <div
                    key="form"
                    className="App"
                    style={formStyles}
                >
                    <RowDiv
                        colGap={gap}
                        style={{ flexFlow: "column", maxHeight: "100%", overflow: "hidden", display: "flex" }}
                    >
                        {firstContainer && (
                            <ContainerDiv colGap={gap}>
                                <XContainerDesignWrapper
                                    id={firstContainer.id}
                                    index={0}
                                    innerComponent={firstContainer}
                                    editContext={this.getFormContext()!}
                                />
                            </ContainerDiv>
                        )}
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                position: pinned ? "relative" : "static",
                            }}
                            data-testid="no-pinned-section"
                        >
                            {scrollableContrainers.map((c, i) => {
                                return (
                                    <React.Fragment key={c.id}>
                                        {this.showLinesBetweenContainers && (firstContainer || i !== 0) && (
                                            <ContainerDivider
                                                data-testid="line-between-containers"
                                                lineColorBetweenContainers={this.defaultLineBetweenContainerColor}
                                                lineStyleBetweenContrainers={this.defaultLineBetweenContainerStyle}
                                                lineWidthBetweenContainers={this.defaultLineBetweenContainerWidth}
                                                colGap={gap}
                                            />
                                        )}
                                        <ContainerDiv colGap={gap}>
                                            <XContainerDesignWrapper
                                                id={c.id}
                                                index={firstContainer ? i + 1 : i}
                                                innerComponent={c}
                                                editContext={this.getFormContext()!}
                                            />
                                        </ContainerDiv>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        {lastContainer && (
                            <ContainerDiv colGap={gap}>
                                <>
                                    {this.showLinesBetweenContainers && (
                                        <ContainerDivider
                                            data-testid="line-between-containers"
                                            lineColorBetweenContainers={this.defaultLineBetweenContainerColor}
                                            lineStyleBetweenContrainers={this.defaultLineBetweenContainerStyle}
                                            lineWidthBetweenContainers={this.defaultLineBetweenContainerWidth}
                                            colGap={gap}
                                        />
                                    )}
                                    <XContainerDesignWrapper
                                        id={lastContainer.id}
                                        index={this.containers.length - 1}
                                        innerComponent={lastContainer}
                                        editContext={this.getFormContext()!}
                                    />
                                </>
                            </ContainerDiv>
                        )}
                    </RowDiv>
                </div>
            );
        } else {
            return (
                <div
                    key="form"
                    className="App"
                    style={formStyles}
                >
                    <RowDiv
                        colGap={gap}
                        hasOverflow={
                            mode !== FormMode.Runtime &&
                            !(this.doNotScrollLastContainerOnForm || this.doNotScrollFirstContainerOnForm)
                        }
                    >
                        {firstContainer && (
                            <ContainerSectionDiv
                                pinned={true}
                                data-testid="pin-first-container"
                            >
                                <PinnedNotifyDiv moveRightPX={this.formMargin}>
                                    <IconButton
                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                        size="large"
                                    >
                                        <PinIcon />
                                    </IconButton>
                                </PinnedNotifyDiv>
                                <ContainerDiv colGap={gap}>
                                    <XContainerDesignWrapper
                                        id={firstContainer.id}
                                        index={0}
                                        innerComponent={firstContainer}
                                        editContext={this.getFormContext()!}
                                    />
                                </ContainerDiv>
                            </ContainerSectionDiv>
                        )}
                        <ContainerSectionDiv pinned={pinned}>
                            {pinned && (
                                <PinnedNotifyDiv
                                    color={UIStyleConstants.RED_DARK_COLOR}
                                    moveRightPX={this.formMargin}
                                    data-testid="pin-middle-container"
                                >
                                    <IconButton
                                        color="secondary"
                                        size="large"
                                    >
                                        <ScrollIcon />
                                    </IconButton>
                                </PinnedNotifyDiv>
                            )}
                            {scrollableContrainers.map((c: XBaseContainer, i: number) => {
                                return (
                                    <React.Fragment key={c.id}>
                                        {this.showLinesBetweenContainers && (firstContainer || i !== 0) && (
                                            <ContainerDivider
                                                data-testid="line-between-containers"
                                                lineColorBetweenContainers={this.defaultLineBetweenContainerColor}
                                                lineStyleBetweenContrainers={this.defaultLineBetweenContainerStyle}
                                                lineWidthBetweenContainers={this.defaultLineBetweenContainerWidth}
                                                colGap={gap}
                                            />
                                        )}
                                        <ContainerDiv colGap={gap}>
                                            <XContainerDesignWrapper
                                                id={c.id}
                                                index={i}
                                                innerComponent={c}
                                                editContext={this.getFormContext()!}
                                            />
                                        </ContainerDiv>
                                    </React.Fragment>
                                );
                            })}
                        </ContainerSectionDiv>
                        {lastContainer && (
                            <>
                                {this.showLinesBetweenContainers && (
                                    <ContainerDivider
                                        data-testid="line-between-containers"
                                        lineColorBetweenContainers={this.defaultLineBetweenContainerColor}
                                        lineStyleBetweenContrainers={this.defaultLineBetweenContainerStyle}
                                        lineWidthBetweenContainers={this.defaultLineBetweenContainerWidth}
                                        colGap={gap}
                                    />
                                )}
                                <ContainerSectionDiv
                                    pinned={true}
                                    data-testid="pin-last-container"
                                >
                                    <PinnedNotifyDiv moveRightPX={this.formMargin}>
                                        <IconButton
                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                            size="large"
                                        >
                                            <PinIcon />
                                        </IconButton>
                                    </PinnedNotifyDiv>
                                    <ContainerDiv colGap={gap}>
                                        <XContainerDesignWrapper
                                            id={lastContainer.id}
                                            index={this.containers.length - 1}
                                            innerComponent={lastContainer}
                                            editContext={this.getFormContext()!}
                                        />
                                    </ContainerDiv>
                                </ContainerSectionDiv>
                            </>
                        )}
                    </RowDiv>
                </div>
            );
        }
    }

    find(id: string): FormControl | null {
        if (!this.containers) return null;

        for (let cont of this.containers) {
            if (cont.id === id) return cont;

            let res = cont.find(id);

            if (res) return res;
        }

        return null;
    }

    getAllControls(): FormControl[] {
        let controls: FormControl[];
        controls = [];

        for (let container of this.containers) {
            controls.push(...container.getControls());
        }

        return controls;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "_id",
            "title",
            "designFormWidth",
            "runtimeWidth",
            "gapBetweenContainers",
            "formMargin",
            "defaultInnerContainerMargin",
            "defaultInnerColumnMargin",
            "defaultInterControlSpacing",
            "doNotScrollLastContainerOnForm",
            "doNotScrollFirstContainerOnForm",
            "defaultShowContainerBorder",
            "defaultContainerBorderRadius",
            "defaultContainerBorderWidth",
            "defaultContainerBorderColor",
            "defaultContainerBorderStyle",
            "showLinesBetweenContainers",
            "defaultLineBetweenContainerWidth",
            "defaultLineBetweenContainerColor",
            "defaultLineBetweenContainerStyle",
            "formBackgroundColor",
            "defaultContainerBackgroundColor",
            "defaultTextStyle",
            "defaultTextSize",
            "defaultButtonSize",
            "defaultLinkUnderline",
            "defaultLinkType",
        );

        let containers: object[] = [];

        for (let container of this.containers) {
            containers.push(container.getStorageData());
        }

        Reflect.set(retData, "__containers", containers);

        let rules: object[] = [];

        for (let r of this._rules) {
            rules.push(r.getStorageData());
        }

        Reflect.set(retData, "rules", rules);

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "_id",
            "title",
            "designFormWidth",
            "runtimeWidth",
            "gapBetweenContainers",
            "formMargin",
            "defaultInnerContainerMargin",
            "defaultInnerColumnMargin",
            "defaultInterControlSpacing",
            "doNotScrollLastContainerOnForm",
            "doNotScrollFirstContainerOnForm",
            "defaultShowContainerBorder",
            "defaultContainerBorderRadius",
            "defaultContainerBorderWidth",
            "defaultContainerBorderColor",
            "defaultContainerBorderStyle",
            "showLinesBetweenContainers",
            "defaultLineBetweenContainerWidth",
            "defaultLineBetweenContainerColor",
            "defaultLineBetweenContainerStyle",
            "formBackgroundColor",
            "defaultContainerBackgroundColor",
            "defaultTextStyle",
            "defaultTextSize",
            "defaultButtonSize",
            "defaultLinkUnderline",
            "defaultLinkType",
        );

        this.containers = [];
        let containerArray = Reflect.get(data, "__containers");

        let editContext: FormContext | null = this.getFormContext();

        if (containerArray && containerArray instanceof Array && containerArray.length !== 0) {
            for (let container of containerArray) {
                let typeName = Reflect.get(container, "__type");

                if (typeName) {
                    let registeredControl = UIControlRegistration[typeName];

                    let val = new registeredControl.prototype.constructor();
                    // @ts-ignore
                    let control = val as XBaseContainer;

                    if (editContext) control.setFormContext(editContext);

                    control.setStorageData(container);
                    this.containers.push(control);
                }
            }
        }

        let ruleData = Reflect.get(data, "rules") as [];

        if (ruleData) {
            for (let r of ruleData) {
                let ruleData = new FormRule(new ExpressionConfiguration());
                ruleData.setStorageData(r);
                this.rules.push(ruleData);
            }
        }
    }

    refreshDesigner = (): void => {
        if (this._formContext) {
            this._formContext?.refreshDesigner();
        }
    };

    renderEditUI(): JSX.Element | null {
        return (
            <UISpacedBoxContent>
                <PropertyEditorBoolean
                    editObject={this}
                    label="Layout Borders"
                    propertyName="defaultShowContainerBorder"
                />
                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"defaultShowContainerBorder"}
                >
                    <PropertyEditorInteger
                        editObject={this}
                        label="Borders Width"
                        propertyName="defaultContainerBorderWidth"
                    />
                    <PropertyEditorInteger
                        editObject={this}
                        label="Borders Radius"
                        propertyName="defaultContainerBorderRadius"
                    />

                    <PropertyEditorColor
                        editObject={this}
                        label="Borders Color"
                        propertyName="defaultContainerBorderColor"
                        parentDefaultValue={FormDesignConstants.FORM_BACKGROUND_COLOR}
                    />
                    <PropertyEditorSelect
                        editObject={this}
                        label="Borders Style"
                        propertyName="defaultContainerBorderStyle"
                        options={FormDesignConstants.BORDER_STYLES}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorColor
                    editObject={this}
                    label="Form Color"
                    propertyName="formBackgroundColor"
                    parentDefaultValue={FormDesignConstants.FORM_BACKGROUND_COLOR}
                />

                <Typography
                    color="textSecondary"
                    display="block"
                    variant="caption"
                    style={{ marginTop: FormDesignConstants.FORM_SECTION_MARGIN }}
                >
                    Pinned Sections
                </Typography>
                <Divider />
                <PropertyEditorBoolean
                    label={"Pin First Container"}
                    editObject={this}
                    propertyName={"doNotScrollFirstContainerOnForm"}
                />
                <PropertyEditorBoolean
                    label={"Pin Last Container"}
                    editObject={this}
                    propertyName={"doNotScrollLastContainerOnForm"}
                />

                <Typography
                    color="textSecondary"
                    display="block"
                    variant="caption"
                    style={{ marginTop: FormDesignConstants.FORM_SECTION_MARGIN }}
                >
                    Form Size
                </Typography>
                <Divider />
                <PropertyEditorInteger
                    label={"Design Form Width"}
                    editObject={this}
                    propertyName={"designFormWidth"}
                />
                <PropertyEditorInteger
                    label={"Runtime Width"}
                    editObject={this}
                    propertyName={"runtimeWidth"}
                />

                <Typography
                    color="textSecondary"
                    display="block"
                    variant="caption"
                    style={{ marginTop: FormDesignConstants.FORM_SECTION_MARGIN }}
                >
                    Spacing
                </Typography>
                <Divider />

                <PropertyEditorInteger
                    label={"Between Layouts"}
                    editObject={this}
                    propertyName={"gapBetweenContainers"}
                    parentDefaultValue={FormDesignConstants.GAP_BETWEEN_CONTAINERS}
                />

                <PropertyEditorBoolean
                    editObject={this}
                    label="Line Between Containers"
                    propertyName="showLinesBetweenContainers"
                />
                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"showLinesBetweenContainers"}
                >
                    <PropertyEditorInteger
                        editObject={this}
                        label="Line Width"
                        propertyName="defaultLineBetweenContainerWidth"
                    />

                    <PropertyEditorColor
                        editObject={this}
                        label="Line Color"
                        propertyName="defaultLineBetweenContainerColor"
                    />
                    <PropertyEditorSelect
                        editObject={this}
                        label="Line Style"
                        propertyName="defaultLineBetweenContainerStyle"
                        options={FormDesignConstants.BORDER_STYLES}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorInteger
                    label={"Around Form"}
                    editObject={this}
                    propertyName={"formMargin"}
                />

                <Typography
                    color="textSecondary"
                    display="block"
                    variant="caption"
                    style={{ marginTop: FormDesignConstants.FORM_SECTION_MARGIN }}
                >
                    Default Text Field Style
                </Typography>
                <Divider />
                <PropertyEditorTextStyleSelection
                    propertyName={"defaultTextStyle"}
                    editObject={this}
                />
                <PropertyEditorTextSizeSelection
                    propertyName={"defaultTextSize"}
                    editObject={this}
                />

                <Typography
                    color="textSecondary"
                    display="block"
                    variant="caption"
                    style={{ marginTop: FormDesignConstants.FORM_SECTION_MARGIN }}
                >
                    Defaults
                </Typography>
                <Divider />

                <PropertyEditorInteger
                    label={"Container Inner Margin"}
                    editObject={this}
                    propertyName={"defaultInnerContainerMargin"}
                />
                <PropertyEditorInteger
                    label={"Column Inner Margin"}
                    editObject={this}
                    propertyName={"defaultInnerColumnMargin"}
                />
                <PropertyEditorInteger
                    label={"Space Between Controls"}
                    editObject={this}
                    propertyName={"defaultInterControlSpacing"}
                />

                <PropertyEditorColor
                    editObject={this}
                    label="Container Color"
                    propertyName="defaultContainerBackgroundColor"
                    parentDefaultValue={FormDesignConstants.CONTAINER_BACKGROUND_COLOR}
                />
            </UISpacedBoxContent>
        );
    }

    // this is all the form data, both input and output
    getAllFormData(): IDataDefinition[] {
        const controls = this.getAllControls();
        const formDataDefs: IDataDefinition[] = [];
        for (const control of controls) {
            const controlDataDef = control.getAddedData();
            if (controlDataDef) {
                if (Array.isArray(controlDataDef)) {
                    formDataDefs.concat(controlDataDef);
                } else {
                    formDataDefs.push(controlDataDef);
                }
            }
        }
        return formDataDefs;
        // todo: needs to be completed
        // - input data
        // - all controls, add the data
        //  - if a value is not a root value, can eliminate it here.  like person.name.
    }

    getOutcomes(): MultiExecutableOutputDefinition[] {
        let result: MultiExecutableOutputDefinition[] = [];

        for (let c of this.containers) {
            for (let cnt of c.getControls()) {
                if (cnt instanceof ButtonFormControl) {
                    // should look at all buttons, and return.  later we will gather the data we need for each button
                    result.push(new MultiExecutableOutputDefinition(cnt.getOutcomeName(), []));
                } else if (cnt instanceof LinkFormControl) {
                    if (cnt.type === LinkControlType.ACTION) {
                        result.push(new MultiExecutableOutputDefinition(cnt.getOutcomeName(), []));
                    }
                }

                //todo: add the data to each
            }
        }

        // todo: if no outcomes are defined, need to add general one
        // todo: what if a piece of data is outside the outcome

        return result;
    }

    getRuleValidationIssues(fieldName: string): ValidationIssue[] {
        const validationIssues: ValidationIssue[] = [];
        const runableRules = this.getRunableRules(fieldName);
        const data = new ExecutionData(this.getFormDatDefinition().value);

        for (const rule of runableRules.filter((rule) => rule.ruleType === FormRuleType.VALIDATION)) {
            const ruleResult = rule.expression.run(data, undefined);
            if (ruleResult) {
                const message = Reflect.get(rule.data!, "validationMessage");
                const variable = Reflect.get(rule.data!, "controlId");
                if (variable) {
                    validationIssues.push(new ValidationIssue(message, variable));
                }
            }
        }
        return validationIssues;
    }

    dataChangeByRule(fieldName: string): void {
        const runableRules = this.getRunableRules(fieldName);

        const data = new ExecutionData(this.getFormDatDefinition().value);

        for (const rule of runableRules.filter((rule) => rule.ruleType === FormRuleType.CHANGE_VALUE)) {
            const ruleResult = rule.expression.run(data, undefined);
            if (ruleResult) {
                const datapath = Reflect.get(rule.data!, "dataPath");
                const value = Reflect.get(rule.data!, "value");
                const valueResult = value.run(data, undefined);
                this.getFormContext()!.setFormDataValue(datapath, valueResult);
            }
        }
    }

    hideControlByRule(): void {
        const runableRules = this.rules;

        const data = new ExecutionData(this.getFormDatDefinition().value);
        let hideControls: string[] = [];
        for (const rule of runableRules.filter((rule) => rule.ruleType === FormRuleType.CONTROL_VISIBILITY)) {
            const ruleResult = rule.expression.run(data, undefined);
            if (ruleResult) {
                const controlId = Reflect.get(rule.data!, "controlId");
                if (typeof controlId === "object") {
                    hideControls = [...hideControls, ...controlId];
                } else {
                    hideControls.push(controlId);
                }
            }
        }
        this.getFormContext()!.runtimeHideControls = hideControls;
    }

    disableControlByRule(): void {
        const runableRules = this.rules;

        const data = new ExecutionData(this.getFormDatDefinition().value);
        let disabledControls: string[] = [];
        for (const rule of runableRules.filter((rule) => rule.ruleType === FormRuleType.CONTROL_ENABLED)) {
            const ruleResult = rule.expression.run(data, undefined);
            if (ruleResult) {
                const controlId = Reflect.get(rule.data!, "controlId");
                if (typeof controlId === "object") {
                    disabledControls = [...disabledControls, ...controlId];
                } else {
                    disabledControls.push(controlId);
                }
            }
        }
        this.getFormContext()!.runtimeDisabledControls = disabledControls;
    }

    focusControlByRule(): void {
        const runableRules = this.rules;

        const data = new ExecutionData(this.getFormDatDefinition().value);
        let focusControls: string[] = [];
        for (const rule of runableRules.filter((rule) => rule.ruleType === FormRuleType.CHANGE_FOCUS)) {
            try {
                const ruleResult = rule.expression.run(data, undefined);
                if (ruleResult) {
                    const controlId = Reflect.get(rule.data!, "controlId");
                    if (typeof controlId === "object") {
                        focusControls = [...focusControls, ...controlId];
                    } else {
                        focusControls.push(controlId);
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }

        for (const focusControl of focusControls) {
            const control = this.find(focusControl);
            if (control && control.ref.current) {
                control.focusControl();
            }
        }
    }

    getRunableRules(fieldName: string): FormRule[] {
        const runableRules: FormRule[] = [];
        for (const rule of this.rules) {
            const watchingValuesMode = rule.watchingValuesMode;
            let watchingValues = rule.watchingValues;
            if (watchingValuesMode === "value used in rule") {
                watchingValues = rule.expression.getUsedVariables();
            }

            if (
                rule.enabled &&
                watchingValues.indexOf(fieldName) > -1 &&
                rule.getDesignValidationIssues(this).length === 0
            ) {
                runableRules.push(rule);
            }
        }
        return runableRules;
    }

    toString(): string {
        return "Form Config";
    }

    // todo: this needs to be a callback type
    run(_data: ExecutionData, _testContext?: ITestingContext): ExecutableReturnPromise {
        throw new Error("Forms cannot be executed directly, they are shown in <FormRuntime.../>");
    }
}
